/**
 * OnboardingGate — conditional mount wrapper for the onboarding wizard
 * (016 Onboarding Wizard, spec §Integration Point, FR-001, FR-008).
 *
 * The single condition `state.kind === 'member' && !state.onboardingCompleted`
 * does triple duty: members who haven't completed see the wizard; members
 * who have completed fall through to `children`; admin and anonymous users
 * always fall through. The admin pass-through and anonymous pass-through
 * are deliberate (admin is elevated capability per Constitution P4;
 * anonymous users hit the per-route `RouteGuard` inside `RouteRegistry`
 * which redirects them to `/login`).
 *
 * `handleComplete` is invoked by both the "Jatka → Siirry palveluun"
 * completion path (US1) and the "Ohita" skip path (US2). It writes to the
 * `SessionPort` and then navigates to `/`.
 *
 * Forward-compat note: the demo adapter's `completeOnboarding()` is
 * synchronous, so the fire-and-navigate sequence works today. When the
 * queued identity-and-access feature ships an async Entra adapter,
 * `handleComplete` migrates to `async`/`await session.completeOnboarding()`
 * before `navigate()` and the wizard's `onComplete: () => void` prop
 * typing widens accordingly to avoid a floating promise.
 */

import type { JSX, ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { useSession } from '../session/useSession.js';

import { OnboardingWizard } from './OnboardingWizard.js';

export interface OnboardingGateProps {
  readonly children: ReactNode;
}

export function OnboardingGate({ children }: OnboardingGateProps): JSX.Element {
  const session = useSession();
  const navigate = useNavigate();

  const handleComplete = (): void => {
    session.completeOnboarding();
    navigate('/', { replace: true });
    // Radix Dialog's focus-restoration targets the trigger element, but the
    // wizard opens without a trigger (controlled `open` prop). After the
    // dialog unmounts, focus would drop to `document.body`. Schedule a
    // `requestAnimationFrame` so React commits the Home page first, then
    // move focus to the `<main id="main-content">` landmark (the same
    // target the SkipLink uses) so keyboard/screen-reader users land on
    // the content region instead of the document root.
    requestAnimationFrame(() => {
      const main = document.getElementById('main-content');
      if (main) {
        main.focus({ preventScroll: true });
      }
    });
  };

  if (session.state.kind === 'member' && !session.state.onboardingCompleted) {
    return <OnboardingWizard onComplete={handleComplete} />;
  }

  // Pass-through for admin (elevated capability — P4), anonymous (RouteGuard
  // handles redirect), and completed members.
  return <>{children}</>;
}
